import Box from '@mui/material/Box'
import { palettePrimaryDark, palettePrimaryMain as PRIMARY } from '@/theme/designTokens'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'

import { UnlockModalLockPasswordIcon } from './UnlockModalLockPasswordIcon'

/** Figma Tour 18 / Container (4383:4104) */
const MODAL_WIDTH_PX = 448
const MODAL_MIN_HEIGHT_PX = 329
const ICON_ROW_HEIGHT_PX = 112
const BODY_BLOCK_MIN_HEIGHT_PX = 125
const ACTIONS_ROW_MIN_HEIGHT_PX = 84

interface Props {
  open: boolean
  confirmationTitle: string
  fullTitleLabel: string
  previousLoiOrdinal: number
  onClose: () => void
  onConfirm: () => Promise<void>
  confirming: boolean
}

export function UnlockLoiModal({
  open,
  confirmationTitle,
  fullTitleLabel,
  previousLoiOrdinal,
  onClose,
  onConfirm,
  confirming,
}: Readonly<Props>) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '10px',
          width: {
            xs: 'min(100%, calc(100vw - 32px))',
            sm: MODAL_WIDTH_PX,
          },
          maxWidth: {
            xs: 'min(100%, calc(100vw - 32px))',
            sm: MODAL_WIDTH_PX,
          },
          minHeight: MODAL_MIN_HEIGHT_PX,
          m: 2,
          p: 0,
          boxShadow:
            '0px 20px 12.5px rgba(0,0,0,0.1), 0px 8px 5px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      }}
      aria-labelledby="unlock-loi-title"
      data-testid="launch-unlock-loi-modal"
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          '&.MuiDialogContent-root': { pt: 0 },
        }}
      >
        {/* Figma 4383:4105 — icon row: h 112, pt 32 */}
        <Box
          sx={{
            height: ICON_ROW_HEIGHT_PX,
            flexShrink: 0,
            pt: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            boxSizing: 'border-box',
          }}
          aria-hidden
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: '#F1FAFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: PRIMARY,
            }}
          >
            <UnlockModalLockPasswordIcon sx={{ fontSize: 24 }} />
          </Box>
        </Box>

        {/* Figma 4383:4108 — copy block: px 24, gap 12, min h 125 */}
        <Box
          sx={{
            px: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            minHeight: BODY_BLOCK_MIN_HEIGHT_PX,
            flexShrink: 0,
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          <Box
            id="unlock-loi-title"
            component="h2"
            sx={{
              m: 0,
              minHeight: '28px',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              color: '#23262F',
              letterSpacing: '-0.4492px',
            }}
          >
            {confirmationTitle}
          </Box>
          <Box
            component="p"
            sx={{
              m: 0,
              maxWidth: '388px',
              mx: 'auto',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '22.75px',
              color: '#4A5565',
              letterSpacing: '-0.1504px',
            }}
          >
            Are you sure you want to manually unlock {fullTitleLabel}? You can do
            this before LOI {previousLoiOrdinal} hits its response target.
          </Box>
        </Box>

        {/* Figma 4383:4113 — actions: h 84, px 102, gap 12 */}
        <Box
          sx={{
            minHeight: ACTIONS_ROW_MIN_HEIGHT_PX,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            px: '102px',
            boxSizing: 'border-box',
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={confirming}
            data-testid="launch-unlock-loi-cancel"
            sx={{
              minHeight: 40,
              borderRadius: '12px',
              borderWidth: '1.5px',
              borderColor: '#E5E7EB',
              color: '#6B6B6B',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '16px',
              px: '16px',
              py: '12px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void onConfirm()}
            disabled={confirming}
            data-testid="launch-unlock-loi-confirm"
            sx={{
              minHeight: 40,
              borderRadius: '12px',
              bgcolor: PRIMARY,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '16px',
              px: '16px',
              py: '12px',
              boxShadow: 'none',
              '&:hover': { bgcolor: palettePrimaryDark, boxShadow: 'none' },
            }}
          >
            Yes, Unlock
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
